import { defineStore } from 'pinia'
import { computed, ref, Ref } from 'vue'
import Api from '@/config/api.helper'

export interface UsuarioConfiguracoes {
  [key: string]: unknown
}

export interface UsuarioStoreData {
  usu_id: number
  uuid_usu_id: string
  usu_avatar: string | null
  usu_nome: string
  usu_email: string
  usu_seth: boolean
  usu_senha_automatica: boolean
  configuracoes: UsuarioConfiguracoes | null
}

interface UserStore {
  usuario: Ref<UsuarioStoreData | null>
  isLoading: Ref<boolean>
  isAuthenticatedUser: Ref<boolean>
  setUsuario(newUsuario: UsuarioStoreData | null): void
  clearUsuario(): void
  fetchUsuario(): Promise<UsuarioStoreData | null>
  initialize(): Promise<void>
}

const USER_STORAGE_KEY = 'userStore.usuario'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function normalizeUsuarioPayload(payload: unknown): UsuarioStoreData | null {
  if (!isRecord(payload)) {
    return null
  }

  return {
    usu_id: Number(payload.usu_id ?? 0),
    uuid_usu_id:
      typeof payload.uuid_usu_id === 'string' ? payload.uuid_usu_id : '',
    usu_avatar:
      typeof payload.usu_avatar === 'string' ? payload.usu_avatar : null,
    usu_nome: typeof payload.usu_nome === 'string' ? payload.usu_nome : '',
    usu_email: typeof payload.usu_email === 'string' ? payload.usu_email : '',
    usu_seth: Boolean(payload.usu_seth),
    usu_senha_automatica: Boolean(payload.usu_senha_automatica),
    configuracoes: isRecord(payload.configuracoes) ? payload.configuracoes : null,
  }
}

function extractUsuarioPayload(responseData: unknown): unknown {
  if (!isRecord(responseData)) {
    return responseData
  }

  if ('usuario' in responseData) {
    return responseData.usuario
  }

  if ('data' in responseData) {
    const data = responseData.data
    if (isRecord(data) && 'usuario' in data) {
      return data.usuario
    }

    return data
  }

  return responseData
}

export const useUserStore = defineStore('user', (): UserStore => {
  const usuario = ref<UsuarioStoreData | null>(null)
  const isLoading = ref<boolean>(false)

  const isAuthenticatedUser = computed(
    (): boolean => !!usuario.value?.uuid_usu_id
  )

  function setUsuario(newUsuario: UsuarioStoreData | null): void {
    usuario.value = newUsuario

    if (newUsuario) {
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(newUsuario))
      return
    }

    localStorage.removeItem(USER_STORAGE_KEY)
  }

  function clearUsuario(): void {
    setUsuario(null)
  }

  async function fetchUsuario(): Promise<UsuarioStoreData | null> {
    isLoading.value = true

    try {
      const response = await Api.get<unknown>('v2/usuario')
      const payload = extractUsuarioPayload(response.data)
      const normalizedUsuario = normalizeUsuarioPayload(payload)

      if (!normalizedUsuario) {
        clearUsuario()
        return null
      }

      setUsuario(normalizedUsuario)
      return normalizedUsuario
    } catch (error) {
      //console.error('Erro ao buscar dados do usuario:', error)
      return null
    } finally {
      isLoading.value = false
    }
  }

  async function initialize(): Promise<void> {
    let cachedUsuario = localStorage.getItem(USER_STORAGE_KEY)

    if (!cachedUsuario) {
      await fetchUsuario()
      cachedUsuario = localStorage.getItem(USER_STORAGE_KEY)

      if (!cachedUsuario) {
        return
      }
    }

    try {
      const parsedUsuario = JSON.parse(cachedUsuario) as unknown
      const normalizedUsuario = normalizeUsuarioPayload(parsedUsuario)

      if (!normalizedUsuario) {
        clearUsuario()
        await fetchUsuario()
        return
      }

      usuario.value = normalizedUsuario
    } catch (error) {
      console.error('Erro ao carregar dados do usuario em cache:', error)
      clearUsuario()
      await fetchUsuario()
    }
  }

  return {
    usuario,
    isLoading,
    isAuthenticatedUser,
    setUsuario,
    clearUsuario,
    fetchUsuario,
    initialize,
  }
})